import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useAuth } from '../../hooks/useAuth';
import { showToast, RateLimitInfo } from '../../utils/errorHandler';
import { Brain, MessageSquare, CheckCircle, XCircle, X, Send, ZoomIn, ZoomOut, ArrowLeft, ChevronDown, Bookmark, Briefcase, Star, MapPin } from 'lucide-react';
import { normalizeScore, formatScore } from '../../utils/scoreUtils';
import ReportCVModal from './ReportCVModal';
import RateLimitNotification from '../RateLimitNotification';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '../../contexts/UserProfileContext';

// @ts-ignore - pdfjsLib is loaded from CDN
declare const pdfjsLib: any;

interface CVImages {
  cv_id: string;
  images: string[];
  image_count: number;
}

interface CVKeywords {
  cv_id: string;
  keywords: string[];
  sections: Record<string, string[]>;
}

interface CandidateInsights {
  fit_explanation: {
    strengths: string[];
    concerns: string[];
    fit_score: number;
    key_alignment_points: string[];
    summary: string;
  };
  job_information: {
    job_title: string;
    nice_to_know?: string;
  };
  personalized_message: {
    subject: string;
    body: string;
    key_points: string[];
  };
}

interface CandidateCVViewerProps {
  cvId: string;
  images: CVImages | null;
  keywords: CVKeywords | null;
  score: number;
  jobDescriptionFile?: File | null; // The uploaded job description file
  cachedInsights?: CandidateInsights | null; // Cached insights from parent
  cachedInsightsHash?: string | null; // Hash of the job description used for cached insights
  onInsightsGenerated?: (insights: CandidateInsights, hash: string) => void; // Callback to save insights to parent
  // Recruiter preferences (optional)
  customRequirements?: string;
  targetedLocations?: string;
  targetedRolePreferences?: string;
  targetedCurrentRole?: string;
  targetedCurrentLevel?: string;
  onBack?: () => void;
  candidateCurrentRole?: string;
  candidateCurrentLevel?: string;
  candidateBaseLocations?: string;
  candidateRolePreferences?: string;
  candidatePreferredLocations?: string;
  showSave?: boolean;
  isSaved?: boolean;
  onToggleSave?: (e: React.MouseEvent) => void;
  // Reach-out status (from Candidate Search - already contacted)
  reachOutStatus?: string;
  reachOutId?: string;
  conversationId?: string;
  onReachOutSuccess?: (data: { conversation_id: string; reach_out_id: string }) => void;
  onStatusChange?: (status: string) => void;
}

const CandidateCVViewer: React.FC<CandidateCVViewerProps> = ({ 
  cvId, 
  images, 
  keywords, 
  score, 
  jobDescriptionFile,
  cachedInsights,
  cachedInsightsHash,
  onInsightsGenerated,
  customRequirements,
  targetedLocations,
  targetedRolePreferences,
  targetedCurrentRole,
  targetedCurrentLevel,
  onBack,
  candidateCurrentRole,
  candidateCurrentLevel,
  candidateBaseLocations,
  candidateRolePreferences,
  candidatePreferredLocations,
  showSave = false,
  isSaved = false,
  onToggleSave,
  reachOutStatus,
  reachOutId,
  conversationId,
  onReachOutSuccess,
  onStatusChange
}) => {
  const { t } = useTranslation('hire');
  const { t: tInbox } = useTranslation('recruiterInbox');
  const navigate = useNavigate();
  const hasExistingReachOut = !!(reachOutId && conversationId);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const STATUS_OPTIONS = [
    { value: 'candidate_sparked', emoji: '✨', color: 'bg-purple-100 text-purple-700' },
    { value: 'in_talks', emoji: '💬', color: 'bg-blue-100 text-blue-700' },
    { value: 'initial_call_scheduled', emoji: '📞', color: 'bg-cyan-100 text-cyan-700' },
    { value: 'interviewing', emoji: '🎯', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'offer_talks', emoji: '💼', color: 'bg-orange-100 text-orange-700' },
    { value: 'hired', emoji: '🎉', color: 'bg-pink-100 text-pink-700' },
    { value: 'currently_not_best_fit', emoji: '📋', color: 'bg-indigo-100 text-indigo-700' },
  ];
  const currentStatusOption = STATUS_OPTIONS.find((s) => s.value === (reachOutStatus || 'candidate_sparked')) || STATUS_OPTIONS[0];
  const [currentPage, setCurrentPage] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [insights, setInsights] = useState<CandidateInsights | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showInsights, setShowInsights] = useState(false);
  const [jobDescriptionHash, setJobDescriptionHash] = useState<string | null>(null);
  const [thinkingText, setThinkingText] = useState('');
  const [showReachOutModal, setShowReachOutModal] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [showReportSuccessToast, setShowReportSuccessToast] = useState(false);
  const [isSubmittingReachOut, setIsSubmittingReachOut] = useState(false);
  
  // Helper function to get localStorage key for analysis status
  const getAnalysisKey = (hash: string) => `analyzing_cv_${cvId}_${hash}`;
  
  // Check localStorage on mount to restore analysis status if user navigated away and back
  useEffect(() => {
    const checkAnalysisStatus = async () => {
      if (jobDescriptionFile) {
        const hash = await createJobDescriptionHash(jobDescriptionFile);
        const isCurrentlyAnalyzing = localStorage.getItem(getAnalysisKey(hash)) === 'true';
        if (isCurrentlyAnalyzing) {
          setIsAnalyzing(true);
          animateThinkingText('Vee is analyzing...');
        }
      }
    };
    checkAnalysisStatus();
  }, [cvId, jobDescriptionFile]);
  
  // Sync cached insights from parent when switching candidates
  useEffect(() => {
    if (cachedInsights && cachedInsightsHash) {
      setInsights(cachedInsights);
      setJobDescriptionHash(cachedInsightsHash);
      setIsAnalyzing(false); // Stop analyzing state if insights are ready
      // Clear localStorage analyzing flag
      if (jobDescriptionFile) {
        createJobDescriptionHash(jobDescriptionFile).then(hash => {
          localStorage.removeItem(getAnalysisKey(hash));
        });
      }
    } else {
      // Reset insights when switching to a candidate without cached insights
      setInsights(null);
      setJobDescriptionHash(null);
    }
  }, [cvId, cachedInsights, cachedInsightsHash]);
  
  // Get user profile from context (loaded by InboxWidget, no extra API call)
  const { userProfile } = useUserProfile();
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [reachOutForm, setReachOutForm] = useState({
    job_title: '',
    message: '',
    salary_range: '',
    nice_to_know: ''
  });
  
  const { apiCall: authApiCall, user } = useAuth();
  const { i18n } = useTranslation();
  
  // API Base URL from environment variable
  const API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1`;
  
  // Helper function to replace AI-generated greeting with neutral, language-aware greeting
  const replaceGreeting = (message: string): string => {
    const currentLanguage = i18n.language || 'en';
    
    // Define neutral greetings by language
    const greetings: Record<string, string> = {
      'de': 'Guten Tag,',
      'en': 'Dear Candidate,'
    };
    
    const neutralGreeting = greetings[currentLanguage] || greetings['en'];
    
    // AI always generates greeting in first line - simply replace it
    const lines = message.split('\n');
    if (lines.length > 0) {
      lines[0] = neutralGreeting;
      return lines.join('\n');
    }
    
    return message;
  };
  
  // Helper function to replace AI-generated ending with language-appropriate ending
  const replaceEnding = (message: string): string => {
    const currentLanguage = i18n.language || 'en';
    
    // Define endings by language
    const endings: Record<string, string> = {
      'de': 'Beste Grüße,',
      'en': 'Best regards,'
    };
    
    const targetEnding = endings[currentLanguage] || endings['en'];
    
    // Common German endings to detect and replace
    const germanEndings = [
      'Beste Grüße,',
      'beste Grüße,',
      'Mit freundlichen Grüßen,',
      'mit freundlichen Grüßen,',
      'Viele Grüße,',
      'viele Grüße,',
      'Herzliche Grüße,',
      'herzliche Grüße,',
      'Freundliche Grüße,',
      'freundliche Grüße,'
    ];
    
    // Common English endings to detect and replace
    const englishEndings = [
      'Best regards,',
      'best regards,',
      'Kind regards,',
      'kind regards,',
      'Warm regards,',
      'warm regards,',
      'Sincerely,',
      'sincerely,'
    ];
    
    const lines = message.split('\n');
    
    // Find the line with the ending (usually 3-4 lines before the end)
    for (let i = Math.max(0, lines.length - 5); i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if this line matches any German ending
      if (germanEndings.some(ending => line === ending || line.startsWith(ending))) {
        lines[i] = targetEnding;
        break;
      }
      
      // Check if this line matches any English ending
      if (englishEndings.some(ending => line === ending || line.startsWith(ending))) {
        lines[i] = targetEnding;
        break;
      }
    }
    
    return lines.join('\n');
  };
  
  
  // Wrapper for apiCall that handles response parsing
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
    const response = await authApiCall(fullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'API Error' }));
      const err: any = new Error(error.detail || 'API Error');
      err.status = response.status; // Attach status code for error handling
      
      // For 429 errors, attach rate limit info if available
      if (response.status === 429 && error.rate_limit_info) {
        err.rateLimitInfo = error.rate_limit_info;
      }
      
      throw err;
    }

    return response.json();
  };
  
  // API Helper for FormData requests (like CV analysis)
  const apiCallFormData = async (endpoint: string, options: RequestInit = {}) => {
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
    const response = await authApiCall(fullUrl, options);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'API Error' }));
      const err: any = new Error(error.detail || 'API Error');
      err.status = response.status; // Attach status code for error handling
      throw err;
    }

    return response.json();
  };

  // Load cached insights when component mounts or when cached data changes
  useEffect(() => {
    if (cachedInsights && cachedInsightsHash) {
      setInsights(cachedInsights);
      setJobDescriptionHash(cachedInsightsHash);
    }
  }, [cachedInsights, cachedInsightsHash]);

  // Helper function to create a simple hash of the job description
  const createJobDescriptionHash = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        // Simple hash based on file name, size, and content
        const hash = `${file.name}_${file.size}_${content.length}`;
        resolve(hash);
      };
      reader.readAsText(file);
    });
  };

  // Animate thinking text letter by letter (fixed)
  const animateThinkingText = (text: string) => {
    let currentIndex = 0;
    let isComplete = false;
    
    const animate = () => {
      if (currentIndex < text.length) {
        setThinkingText(text.slice(0, currentIndex + 1));
        currentIndex++;
        setTimeout(animate, 100);
      } else {
        // When complete, wait a bit then restart
        if (!isComplete) {
          isComplete = true;
          setTimeout(() => {
            isComplete = false;
            currentIndex = 0;
            animate();
          }, 1000);
        }
      }
    };
    
    animate();
  };

  if (!images || !keywords) {
    return (
      <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 min-h-[60vh]">
        <div className="flex-1 p-3 sm:p-6">
          <div className="aspect-[210/297] max-h-[65vh] mx-auto bg-white rounded-lg border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-pink-50/60 to-transparent z-10" />
            <div className="p-6 sm:p-10 space-y-5">
              <div className="h-5 w-2/5 bg-slate-200 rounded" />
              <div className="space-y-2.5">
                <div className="h-3 w-full bg-slate-100 rounded" />
                <div className="h-3 w-5/6 bg-slate-100 rounded" />
                <div className="h-3 w-4/6 bg-slate-100 rounded" />
              </div>
              <div className="h-4 w-1/3 bg-slate-200 rounded mt-6" />
              <div className="space-y-2.5">
                <div className="h-3 w-full bg-slate-100 rounded" />
                <div className="h-3 w-4/5 bg-slate-100 rounded" />
                <div className="h-3 w-3/5 bg-slate-100 rounded" />
              </div>
              <div className="h-4 w-1/4 bg-slate-200 rounded mt-6" />
              <div className="space-y-2.5">
                <div className="h-3 w-full bg-slate-100 rounded" />
                <div className="h-3 w-5/6 bg-slate-100 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const skillColors = [
    'bg-gradient-to-r from-blue-500 to-blue-600',
    'bg-gradient-to-r from-emerald-500 to-green-600',
    'bg-gradient-to-r from-purple-500 to-violet-600',
    'bg-gradient-to-r from-orange-500 to-red-500',
    'bg-gradient-to-r from-cyan-500 to-teal-600',
    'bg-gradient-to-r from-pink-500 to-rose-600',
    'bg-gradient-to-r from-indigo-500 to-purple-600',
    'bg-gradient-to-r from-amber-500 to-orange-600',
    'bg-gradient-to-r from-lime-500 to-green-500',
    'bg-gradient-to-r from-fuchsia-500 to-pink-600',
    'bg-gradient-to-r from-sky-500 to-blue-600',
    'bg-gradient-to-r from-red-500 to-pink-500',
  ];

  const getScoreColor = (rawScore: number) => {
    const normalized = normalizeScore(rawScore);
    if (normalized >= 90) return 'from-green-500 to-emerald-500';
    if (normalized >= 80) return 'from-blue-500 to-cyan-500';
    if (normalized >= 70) return 'from-orange-500 to-yellow-500';
    return 'from-red-500 to-pink-500';
  };

  const displayScore = formatScore(score);
  const hasMultiplePages = images.images && images.images.length > 1;

  const nextPage = () => {
    if (images.images && currentPage < images.images.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const convertPDFToImages = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // @ts-ignore - pdfjsLib is loaded from CDN
          const pdf = await pdfjsLib.getDocument({ data: e.target?.result }).promise;
          
          // Convert first page only for job description
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 1.5 });
          
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          
          await page.render({
            canvasContext: context!,
            viewport: viewport
          }).promise;
          
          // Convert canvas to blob and create File
          canvas.toBlob((blob) => {
            if (blob) {
              const pngFile = new File([blob], 'job_description.png', { type: 'image/png' });
              resolve(pngFile);
            } else {
              reject(new Error('Failed to convert PDF to PNG'));
            }
          }, 'image/png');
          
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleGenerateInsights = async () => {
    if (!jobDescriptionFile) {
      setError('No job description file available. Please upload a job description first.');
      return;
    }

    // Check if we already have insights for this job description
    const currentHash = await createJobDescriptionHash(jobDescriptionFile);
    
    // First check parent cache
    if (cachedInsights && cachedInsightsHash === currentHash) {
      setInsights(cachedInsights);
      setJobDescriptionHash(cachedInsightsHash);
      setShowInsights(true);
      return;
    }
    
    // Then check local cache
    if (insights && jobDescriptionHash === currentHash) {
      // We already have insights for this job description, just show them
      setShowInsights(true);
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setInsights(null);
    
    // Store in localStorage so status persists if user navigates away and back
    localStorage.setItem(getAnalysisKey(currentHash), 'true');
    
    // Start thinking animation
    animateThinkingText('Vee is analyzing...');

    try {
      let fileToSend = jobDescriptionFile;
      
      // Convert PDF to PNG if needed
      if (jobDescriptionFile.type === 'application/pdf') {
        console.debug('Converting PDF to PNG...');
        fileToSend = await convertPDFToImages(jobDescriptionFile);
        console.debug('PDF converted to PNG');
      }

      const formData = new FormData();
      formData.append('cv_id', cvId);
      formData.append('job_description', fileToSend);
      
      // Add optional recruiter preferences if provided
      if (customRequirements?.trim()) {
        formData.append('custom_requirements', customRequirements);
      }
      if (targetedLocations?.trim()) {
        formData.append('targeted_locations', targetedLocations);
      }
      if (targetedRolePreferences?.trim()) {
        formData.append('targeted_role_preferences', targetedRolePreferences);
      }
      if (targetedCurrentRole?.trim()) {
        formData.append('targeted_current_role', targetedCurrentRole);
      }
      if (targetedCurrentLevel?.trim()) {
        formData.append('targeted_current_level', targetedCurrentLevel);
      }

      const response = await apiCallFormData('/exploration/candidate-analysis', {
        method: 'POST',
        body: formData,
      });

      // apiCallFormData already returns parsed JSON
      console.debug('Response data:', response);
      console.debug('Response structure:', JSON.stringify(response, null, 2));
      
      // Handle different response structures
      if (response.insights) {
        setInsights(response.insights);
      } else if (response.fit_explanation && response.personalized_message) {
        // Direct insights structure
        setInsights(response);
      } else {
        setInsights(response);
      }
      
      // Store the job description hash for caching
      setJobDescriptionHash(currentHash);
      
      // Save to parent cache
      if (onInsightsGenerated) {
        const insightsToSave = response.insights || response;
        onInsightsGenerated(insightsToSave, currentHash);
      }
      
      setShowInsights(true);
    } catch (err: any) {
      console.error('Error generating insights:', err);
      
      // Show user-friendly error messages
      if (err.message && err.message.includes('429')) {
        setError('Too many analysis requests. Please wait 30 seconds and try again.');
        showToast('Too many requests. Please wait 30 seconds and try again.', 'error', 6000);
      } else if (err.message && err.message.includes('503')) {
        setError('Analysis service temporarily unavailable. Try again in a moment.');
        showToast('Service temporarily unavailable. Try again in a moment.', 'error');
      } else if (err.message && err.message.includes('504')) {
        setError('Analysis timed out. Please try again.');
        showToast('Analysis timed out. Please try again.', 'error');
      } else {
        setError('Failed to generate insights. Please try again.');
        showToast('Analysis failed. Please check your connection and try again.', 'error');
      }
    } finally {
      setIsAnalyzing(false);
      // Clear localStorage flag when analysis completes (success or error)
      localStorage.removeItem(getAnalysisKey(currentHash));
    }
  };

  const handleReachOut = async () => {
    // Use profile from context (loaded by InboxWidget, no extra API call needed)
    const profileToUse = userProfile ? {
      first_name: userProfile.first_name,
      last_name: userProfile.last_name,
      company_name: userProfile.company_name
    } : null;
    
    if (!profileToUse) {
      console.warn('⚠️ User profile not available from context');
    } else {
      console.debug('✅ Using profile from context:', profileToUse.first_name, profileToUse.last_name, profileToUse.company_name);
    }
    
    if (insights?.personalized_message) {
      // Pre-fill form with data from insights (salary_range still needs manual input)
      // Replace AI-generated greeting and signature with real recruiter data
      let processedMessage = insights.personalized_message.body || '';
      console.debug('📝 Original message:', processedMessage);
      processedMessage = replaceGreeting(processedMessage);  // Replace greeting
      console.debug('📝 After greeting replacement:', processedMessage);
      processedMessage = replaceEnding(processedMessage);  // Replace ending
      console.debug('📝 After ending replacement:', processedMessage);
      
      // Pass profile directly to replaceSignature instead of using state
      const lines = processedMessage.split('\n');
      let closingIndex = -1;
      for (let i = lines.length - 1; i >= 0; i--) {
        if (lines[i].trim().endsWith(',')) {
          closingIndex = i;
          break;
        }
      }
      
      console.debug('🔍 Closing index:', closingIndex, 'Total lines:', lines.length);
      console.debug('👤 Profile to use:', profileToUse);
      
      if (closingIndex !== -1 && closingIndex < lines.length - 1) {
        lines.splice(closingIndex + 1);
        
        if (profileToUse) {
          const fullName = [profileToUse.first_name, profileToUse.last_name]
            .filter(Boolean)
            .join(' ');
          
          if (fullName) {
            lines.push(fullName);
          }
          
          if (profileToUse.company_name) {
            lines.push(profileToUse.company_name);
          }
          
          console.debug('✅ Added signature:', fullName, profileToUse.company_name);
        } else {
          console.debug('⚠️ profileToUse is null!');
        }
        
        processedMessage = lines.join('\n');
      } else {
        console.debug('⚠️ No closing phrase found or invalid index!');
      }
      
      setReachOutForm({
        job_title: insights.job_information?.job_title || '',
        message: processedMessage,
        salary_range: '',
        nice_to_know: insights.job_information?.nice_to_know || ''
      });
    }
    setShowReachOutModal(true);
  };

  const handleDirectReachOut = () => {
    // Open modal with empty form (no pre-fill)
    setReachOutForm({
      job_title: '',
      message: '',
      salary_range: '',
      nice_to_know: ''
    });
    setShowReachOutModal(true);
  };

  const handleReportSuccess = () => {
    setShowReportSuccessToast(true);
    setTimeout(() => setShowReportSuccessToast(false), 5000);
  };

  const handleReachOutSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!reachOutForm.job_title.trim()) {
      showToast('Please provide a job title', 'error');
      return;
    }
    if (!reachOutForm.salary_range.trim()) {
      showToast('Please provide a salary range', 'error');
      return;
    }
    
    setIsSubmittingReachOut(true);
    
    try {
      const requestData = {
        cv_id: cvId,
        job_title: reachOutForm.job_title,
        message: reachOutForm.message,
        salary_range: reachOutForm.salary_range,
        nice_to_know: reachOutForm.nice_to_know || undefined
        // user_id will be added by Gateway from authenticated session
      };

      console.debug('Sending reach out request:', requestData);
      
      const response = await apiCall('/recruiter/reach-out', {
        method: 'POST',
        body: JSON.stringify(requestData),
      });

      if (response?.success) {
        showToast('Reach out created successfully! 🚀', 'success');
        setShowReachOutModal(false);
        setReachOutForm({ job_title: '', message: '', salary_range: '', nice_to_know: '' });
        if (response?.conversation_id && response?.vector_id && onReachOutSuccess) {
          onReachOutSuccess({ conversation_id: response.conversation_id, reach_out_id: response.vector_id });
        }
      } else {
        showToast(`Error: ${response.error || 'Failed to create reach out'}`, 'error');
      }
    } catch (error: any) {
      console.error('Reach out error:', error);
      
      // Show user-friendly error messages based on status code
      if (error.status === 429) {
        // Try to extract rate limit info from error
        try {
          const rateLimitData = error.rateLimitInfo || {
            type: 'subscription_limit',
            feature_used: 0,
            feature_limit: 0,
            global_used: 0,
            global_limit: 55,
            resets_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            subscription_type: 'recruiter_free'
          };
          setRateLimitInfo(rateLimitData);
        } catch (e) {
          showToast('⏰ Rate limit reached! You have used all your reach-outs for this month. Please upgrade your plan or try again next month.', 'error', 8000);
        }
      } else if (error.status === 503) {
        showToast('Service temporarily unavailable. Try again in a moment.', 'error');
      } else if (error.status === 504) {
        showToast('Request timed out. Please try again.', 'error');
      } else {
        showToast(error.message || 'Failed to create reach out. Please try again.', 'error');
      }
    } finally {
      setIsSubmittingReachOut(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white relative">
      
      
      {/* Header */}
      <motion.div 
        className="flex-shrink-0 p-3 sm:p-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex-1 min-w-0 space-y-1.5 sm:space-y-2">
            {(candidateCurrentRole || candidateCurrentLevel || candidateBaseLocations) && (
              <div className="flex items-start gap-1.5 sm:gap-2">
                <Briefcase className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs sm:text-sm text-slate-700">
                  {candidateCurrentRole && <span className="font-medium">{candidateCurrentRole}</span>}
                  {candidateCurrentRole && candidateCurrentLevel && <span className="text-slate-400 mx-1">·</span>}
                  {candidateCurrentLevel && <span className="text-slate-500">{candidateCurrentLevel}</span>}
                  {candidateBaseLocations && (
                    <span className="text-slate-400 ml-1.5">
                      <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 inline -mt-0.5 mr-0.5" />
                      {candidateBaseLocations.length > 30 ? candidateBaseLocations.substring(0, 30) + '...' : candidateBaseLocations}
                    </span>
                  )}
                </div>
              </div>
            )}
            {(candidateRolePreferences || candidatePreferredLocations) && (
              <div className="flex items-start gap-1.5 sm:gap-2">
                <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="flex flex-wrap gap-1 sm:gap-1.5">
                  {candidateRolePreferences && (
                    <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-pink-50 text-pink-600">{candidateRolePreferences}</span>
                  )}
                  {candidatePreferredLocations && (
                    <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                      <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 inline -mt-0.5 mr-0.5" />{candidatePreferredLocations}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          
        </div>
      </motion.div>

      {/* Analysis Action Bar */}
      <motion.div 
        className="flex-shrink-0 bg-gradient-to-r from-blue-50 to-indigo-50 px-2.5 py-2 sm:p-4 border-b border-slate-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.3 }}
      >
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-1.5 sm:gap-3">
          <motion.button
            onClick={handleGenerateInsights}
            disabled={isAnalyzing || !jobDescriptionFile}
            className={`
              flex items-center justify-center gap-1.5 px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-lg font-medium text-white text-[11px] sm:text-sm
              transition-all duration-200 shadow-sm
              ${isAnalyzing
                ? 'bg-gradient-to-r from-pink-500 to-pink-600 opacity-60 cursor-not-allowed'
                : !jobDescriptionFile
                ? 'bg-slate-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 hover:shadow-md'
              }
            `}
            whileHover={!isAnalyzing && jobDescriptionFile ? { scale: 1.02 } : {}}
            whileTap={!isAnalyzing && jobDescriptionFile ? { scale: 0.98 } : {}}
          >
            {isAnalyzing ? (
              <span className="text-white font-mono text-left w-[150px] sm:w-[180px] text-[10px] sm:text-xs">{thinkingText}</span>
            ) : (insights && jobDescriptionHash) || (cachedInsights && cachedInsightsHash) ? (
              <>
                <Brain className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>{t('cvViewer.showAnalysis')}</span>
              </>
            ) : (
              <>
                <Brain className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>{t('cvViewer.candidateAnalysis')}</span>
              </>
            )}
          </motion.button>

          {hasExistingReachOut ? (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setStatusDropdownOpen((v) => !v); }}
                  className={`flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1 sm:py-1.5 rounded-lg border text-[10px] sm:text-xs font-medium ${currentStatusOption.color} border-current`}
                >
                  <span>{currentStatusOption.emoji}</span>
                  <span>{tInbox(`statusLabels.${currentStatusOption.value}`)}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {statusDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setStatusDropdownOpen(false)} />
                    <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 py-1 min-w-[180px]">
                      {STATUS_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={async (e) => {
                            e.stopPropagation();
                            setStatusDropdownOpen(false);
                            if (!reachOutId) return;
                            try {
                              await apiCall('/recruiter/reach-out/status', {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ vector_id: reachOutId, status: opt.value }),
                              });
                              onStatusChange?.(opt.value);
                            } catch {
                              showToast(t('errors.statusUpdateFailed') || 'Status update failed', 'error');
                            }
                          }}
                          className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-slate-50 ${opt.value === (reachOutStatus || 'candidate_sparked') ? 'bg-slate-50 font-medium' : ''}`}
                        >
                          <span>{opt.emoji}</span>
                          <span>{tInbox(`statusLabels.${opt.value}`)}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <motion.button
                onClick={() => navigate(`/recruiter-inbox?conversation=${conversationId}`)}
                className="flex items-center justify-center gap-1.5 px-2.5 py-1.5 sm:px-3.5 sm:py-2 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-medium rounded-lg transition-all shadow-sm text-[11px] sm:text-sm"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.3 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <MessageSquare className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
                <span>{t('cvViewer.goToChat')}</span>
              </motion.button>
            </div>
          ) : (
            <motion.button
              onClick={handleDirectReachOut}
              className="flex items-center justify-center gap-1.5 px-2.5 py-1.5 sm:px-3.5 sm:py-2 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-all border border-gray-300 hover:border-gray-400 shadow-sm text-[11px] sm:text-sm"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.3 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <MessageSquare className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
              <span>{t('cvViewer.customReachOut')}</span>
            </motion.button>
          )}
        </div>
        
        {!jobDescriptionFile && (
          <div className="mt-2 text-sm text-amber-600 flex items-center gap-2">
            <XCircle size={16} />
            <span>{t('cvViewer.uploadJobDescription')}</span>
          </div>
        )}
        
        {error && (
          <div className="mt-2 text-sm text-red-600 flex items-center gap-2">
            <XCircle size={16} />
            <span>{error}</span>
          </div>
        )}
      </motion.div>
      {/* Insights Panel - Compact Overlay */}
      <AnimatePresence>
        {showInsights && insights && (
          <motion.div
            className="absolute top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm overflow-y-auto max-h-[50vh] sm:max-h-[40vh] shadow-lg border-b border-slate-200"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="p-2.5 sm:p-3">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <CheckCircle className="text-green-600 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <h3 className="text-sm sm:text-base font-semibold text-slate-800">{t('cvViewer.aiAnalysisResults')}</h3>
                </div>
                <button
                  onClick={() => setShowInsights(false)}
                  className="text-slate-500 hover:text-slate-700 p-1 rounded-full hover:bg-slate-100 transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-2.5 sm:space-y-4">
                {/* Fit Analysis */}
                <div className="bg-white rounded-lg p-2.5 sm:p-3 shadow-sm border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-base">
                    <Brain className="text-blue-600 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {t('cvViewer.candidateFitAnalysis')}
                  </h4>
                  
                  <div className="space-y-2 sm:space-y-3">
                    <div>
                      <span className="text-xs sm:text-sm font-medium text-slate-600">{t('cvViewer.keyStrengths')}</span>
                      <ul className="mt-0.5 sm:mt-1 space-y-0.5 sm:space-y-1">
                        {insights.fit_explanation.strengths.map((strength, index) => (
                          <li key={index} className="text-xs sm:text-sm text-slate-700 flex items-start gap-1.5 sm:gap-2">
                            <span className="text-green-500 mt-1">✓</span>
                            <span 
                              className="whitespace-pre-wrap"
                              dangerouslySetInnerHTML={{ 
                                __html: strength.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              }}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {insights.fit_explanation.concerns && insights.fit_explanation.concerns.length > 0 && (
                      <div>
                        <span className="text-xs sm:text-sm font-medium text-slate-600">{t('cvViewer.potentialConcerns')}</span>
                        <ul className="mt-0.5 sm:mt-1 space-y-0.5 sm:space-y-1">
                          {insights.fit_explanation.concerns.map((concern, index) => (
                            <li key={index} className="text-xs sm:text-sm text-slate-700 flex items-start gap-1.5 sm:gap-2">
                              <span className="text-orange-500 mt-1">⚠</span>
                              <span 
                                className="whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{ 
                                  __html: concern.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                }}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {insights.fit_explanation.key_alignment_points && insights.fit_explanation.key_alignment_points.length > 0 && (
                      <div>
                        <span className="text-xs sm:text-sm font-medium text-slate-600">{t('cvViewer.keyAlignmentPoints')}</span>
                        <ul className="mt-0.5 sm:mt-1 space-y-0.5 sm:space-y-1">
                          {insights.fit_explanation.key_alignment_points.map((point, index) => (
                            <li key={index} className="text-xs sm:text-sm text-slate-700 flex items-start gap-1.5 sm:gap-2">
                              <span className="text-blue-500 mt-1">→</span>
                              <span 
                                className="whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{ 
                                  __html: point.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                }}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div>
                      <span className="text-xs sm:text-sm font-medium text-slate-600">{t('cvViewer.summary')}</span>
                      <div 
                        className="text-xs sm:text-sm text-slate-700 mt-0.5 sm:mt-1 leading-relaxed whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ 
                          __html: insights.fit_explanation.summary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Personalized Message */}
                <div className="bg-white rounded-lg p-2.5 sm:p-3 shadow-sm border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-base">
                    <MessageSquare className="text-pink-600 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {t('cvViewer.personalizedMessage')}
                  </h4>
                  
                  <div className="space-y-2 sm:space-y-3">
                    <div>
                      <span className="text-xs sm:text-sm font-medium text-slate-600">{t('cvViewer.message')}</span>
                      <div 
                        className="text-xs sm:text-sm text-slate-700 mt-0.5 sm:mt-1 leading-relaxed whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ 
                          __html: insights.personalized_message.body.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        }}
                      />
                    </div>
                    
                    {/* Reach Out / Go to Chat Button */}
                    <div className="mt-2 sm:mt-4 pt-2 sm:pt-3 border-t border-slate-200">
                      {hasExistingReachOut ? (
                        <button
                          onClick={() => navigate(`/recruiter-inbox?conversation=${conversationId}`)}
                          className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5 sm:gap-2 shadow-sm text-xs sm:text-sm"
                        >
                          <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          {t('cvViewer.goToChat')}
                        </button>
                      ) : (
                        <button
                          onClick={handleReachOut}
                          className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5 sm:gap-2 shadow-sm text-xs sm:text-sm"
                        >
                          <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          {t('cvViewer.reachOut')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CV Image Viewer */}
      <div className="flex-1 flex flex-col min-h-0 pb-4">
        <motion.div 
          className="flex-1 bg-slate-50 rounded-lg m-2 sm:m-4 overflow-hidden shadow-lg border border-slate-200"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          {/* Image Container */}
          <div className="h-full flex flex-col">
            <div className="flex-1 relative bg-white overflow-auto flex items-start justify-center">
              <AnimatePresence mode="wait">
                {images.images && images.images[currentPage] ? (
                  <TransformWrapper
                    key={currentPage}
                    initialScale={1}
                    minScale={0.5}
                    maxScale={3}
                    centerOnInit={false}
                    wheel={{ disabled: true }}
                    doubleClick={{ mode: "reset" }}
                  >
                    {({ zoomIn, zoomOut, resetTransform }) => (
                      <>
                        {/* Zoom Controls - compact horizontal strip */}
                        <div className="absolute top-1.5 right-1.5 sm:top-2.5 sm:right-2.5 z-10 inline-flex items-center rounded-md bg-white/95 shadow-md border border-slate-200/90 p-0.5 gap-0.5">
                          <button
                            onClick={() => zoomIn(0.3)}
                            className="p-1 sm:p-1.5 rounded hover:bg-slate-100 active:bg-slate-200 transition-colors touch-manipulation"
                            title="Zoom In"
                            type="button"
                          >
                            <ZoomIn className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-600" />
                          </button>
                          <button
                            onClick={() => zoomOut(0.3)}
                            className="p-1 sm:p-1.5 rounded hover:bg-slate-100 active:bg-slate-200 transition-colors touch-manipulation"
                            title="Zoom Out"
                            type="button"
                          >
                            <ZoomOut className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-600" />
                          </button>
                        </div>

                        {/* Image with Zoom */}
                        <TransformComponent
                          wrapperStyle={{
                            width: '100%',
                            height: '100%',
                          }}
                          contentStyle={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            paddingTop: '0.75rem',
                            boxSizing: 'border-box',
                          }}
                        >
                          <motion.img
                            src={`data:image/png;base64,${images.images[currentPage]}`}
                            alt={`CV Page ${currentPage + 1}`}
                            className="max-w-full max-h-full object-contain object-top"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            style={{
                              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            }}
                          />
                        </TransformComponent>
                      </>
                    )}
                  </TransformWrapper>
                ) : (
                  <div className="text-center text-slate-500">
                    <div className="text-3xl mb-4">📄</div>
                    <div className="text-lg">{t('cvViewer.cvImageNotAvailable')}</div>
                  </div>
                )}
              </AnimatePresence>
            </div>

            {/* Navigation Controls */}
            {hasMultiplePages && images.images && (
              <motion.div 
                className="flex-shrink-0 bg-white px-2 sm:px-6 py-1.5 sm:py-4 flex items-center justify-between gap-1 border-t border-slate-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.3 }}
              >
                <motion.button
                  onClick={prevPage}
                  disabled={currentPage === 0}
                  className="flex items-center gap-1 sm:gap-3 px-2 py-1 sm:px-6 sm:py-3 bg-white text-slate-700 rounded-md sm:rounded-lg border border-slate-200 sm:border-2 sm:border-slate-300
                           disabled:opacity-50 disabled:cursor-not-allowed
                           hover:bg-slate-50 hover:border-slate-300 sm:hover:border-slate-400 transition-all duration-200 shadow-sm text-xs sm:text-base touch-manipulation"
                  whileHover={currentPage > 0 ? { scale: 1.02 } : {}}
                  whileTap={currentPage > 0 ? { scale: 0.98 } : {}}
                >
                  <span className="text-sm sm:text-lg">◀</span>
                  <span className="font-semibold hidden sm:inline">{t('cvViewer.previous')}</span>
                </motion.button>

                <div className="flex items-center gap-1 sm:gap-3 text-slate-700 font-semibold bg-slate-100 px-2 sm:px-4 py-1 sm:py-2 rounded-md sm:rounded-lg text-[10px] sm:text-base tabular-nums">
                  <span className="text-sm sm:text-lg hidden sm:inline">📄</span>
                  <span>{currentPage + 1}/{images.images.length}</span>
                </div>

                <motion.button
                  onClick={nextPage}
                  disabled={currentPage === images.images.length - 1}
                  className="flex items-center gap-1 sm:gap-3 px-2 py-1 sm:px-6 sm:py-3 bg-white text-slate-700 rounded-md sm:rounded-lg border border-slate-200 sm:border-2 sm:border-slate-300
                           disabled:opacity-50 disabled:cursor-not-allowed
                           hover:bg-slate-50 hover:border-slate-300 sm:hover:border-slate-400 transition-all duration-200 shadow-sm text-xs sm:text-base touch-manipulation"
                  whileHover={currentPage < images.images.length - 1 ? { scale: 1.02 } : {}}
                  whileTap={currentPage < images.images.length - 1 ? { scale: 0.98 } : {}}
                >
                  <span className="font-semibold hidden sm:inline">{t('cvViewer.next')}</span>
                  <span className="text-sm sm:text-lg">▶</span>
                </motion.button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Reach Out Modal */}
      <AnimatePresence>
        {showReachOutModal && (
          <motion.div
            className="fixed inset-0 bg-black/50 z-[10001] flex items-end sm:items-center justify-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowReachOutModal(false)}
          >
            <motion.div
              className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl sm:max-w-lg w-full max-h-[90vh] overflow-y-auto"
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle (mobile) */}
              <div className="flex justify-center pt-2 pb-0 sm:hidden">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>

              {/* Modal Header */}
              <div className="flex items-center justify-between px-4 py-3 sm:p-5 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-gradient-to-r from-pink-500 to-pink-600 rounded-lg">
                    <MessageSquare className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-sm sm:text-lg font-bold text-gray-900">{t('cvViewer.reachOutToCandidate')}</h2>
                  </div>
                </div>
                <button
                  onClick={() => setShowReachOutModal(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Modal Content */}
              <form onSubmit={handleReachOutSubmit} className="px-4 py-3 sm:p-5 space-y-3 sm:space-y-4">
                {/* Job Title */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('cvViewer.jobTitle')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={reachOutForm.job_title}
                    onChange={(e) => setReachOutForm(prev => ({ ...prev, job_title: e.target.value }))}
                    className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 text-sm"
                    placeholder={t('cvViewer.jobTitlePlaceholder')}
                    maxLength={300}
                    required
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {t('cvViewer.jobTitleFormat')}
                  </p>
                </div>

                {/* Salary Range */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('cvViewer.salaryRange')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={reachOutForm.salary_range}
                    onChange={(e) => setReachOutForm(prev => ({ ...prev, salary_range: e.target.value }))}
                    className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 text-sm"
                    placeholder={t('cvViewer.salaryRangePlaceholder')}
                    maxLength={100}
                    required
                  />
                  <p className={`text-[10px] mt-0.5 font-medium ${
                    reachOutForm.salary_range.length > 90 
                      ? 'text-red-600' 
                      : reachOutForm.salary_range.length > 80 
                      ? 'text-yellow-600' 
                      : 'text-gray-400'
                  }`}>
                    {reachOutForm.salary_range.length}/100
                  </p>
                </div>

                {/* Nice to Know */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('cvViewer.niceToKnow')} <span className="text-gray-400">{t('cvViewer.optional')}</span>
                  </label>
                  <textarea
                    value={reachOutForm.nice_to_know}
                    onChange={(e) => setReachOutForm(prev => ({ ...prev, nice_to_know: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 resize-none text-sm"
                    placeholder={t('cvViewer.niceToKnowPlaceholder')}
                    maxLength={500}
                  />
                  <p className={`text-[10px] mt-0.5 font-medium ${
                    reachOutForm.nice_to_know.length > 450 
                      ? 'text-red-600' 
                      : reachOutForm.nice_to_know.length > 400 
                      ? 'text-yellow-600' 
                      : 'text-gray-400'
                  }`}>
                    {reachOutForm.nice_to_know.length}/500
                  </p>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('cvViewer.message').replace(':', '')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={reachOutForm.message}
                    onChange={(e) => setReachOutForm(prev => ({ ...prev, message: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 resize-none text-sm"
                    placeholder={t('cvViewer.messagePlaceholder')}
                    maxLength={3000}
                    required
                  />
                  <p className={`text-[10px] mt-0.5 font-medium ${
                    reachOutForm.message.length > 2700 
                      ? 'text-red-600' 
                      : reachOutForm.message.length > 2400 
                      ? 'text-yellow-600' 
                      : 'text-gray-400'
                  }`}>
                    {reachOutForm.message.length}/3000
                  </p>
                  
                  {/* Message Preview */}
                  {reachOutForm.message && (
                    <div className="mt-2 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-[10px] font-medium text-slate-500 mb-1">{t('cvViewer.preview')}</p>
                      <div 
                        className="text-xs sm:text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ 
                          __html: reachOutForm.message.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="p-2.5 bg-pink-50/60 border border-pink-100 rounded-lg">
                  <p className="text-[11px] sm:text-xs text-gray-600">
                    <strong>💡</strong> {t('explore.reachOutModal.doubleCheckHint', { ns: 'common' })}
                  </p>
                </div>
              </form>

              {/* Footer */}
              <div className="px-4 py-3 sm:p-4 border-t border-gray-100">
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowReachOutModal(false)}
                    disabled={isSubmittingReachOut}
                    className="px-3 py-2 text-xs sm:text-sm text-gray-600 font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('cvViewer.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleReachOutSubmit}
                    disabled={isSubmittingReachOut}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs sm:text-sm bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-medium rounded-lg transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingReachOut ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t('cvViewer.sending')}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>{t('cvViewer.sendMessage')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report CV - subtle link */}
      <div className="flex-shrink-0 px-4 py-2 border-t border-slate-100">
        <button
          className="text-slate-300 hover:text-red-400 text-xs flex items-center gap-1 mx-auto transition-colors"
          onClick={() => setIsReportModalOpen(true)}
        >
          <span className="text-[10px]">⚠️</span>
          <span>{t('cvViewer.reportInappropriateContent')}</span>
        </button>
      </div>

      {/* Success Toast - Report */}
      <AnimatePresence>
        {showReportSuccessToast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[10001] bg-green-500 text-white px-4 py-2.5 rounded-xl shadow-2xl flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-xs sm:text-sm">{t('cvViewer.reportSubmitted')}</p>
              <p className="text-[10px] sm:text-xs text-green-100">{t('cvViewer.reportSuccessMessage')}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report Modal */}
      <ReportCVModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        cvId={cvId}
        onSubmitSuccess={handleReportSuccess}
        apiCall={authApiCall}
      />

      {/* Rate Limit Notification */}
      {rateLimitInfo && (
        <RateLimitNotification
          rateLimitInfo={rateLimitInfo}
          feature="reach_outs"
          onClose={() => setRateLimitInfo(null)}
        />
      )}
    </div>
  );
};

export default CandidateCVViewer;
